import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

import { Modal, ModalContent, Button } from '@restworld/ui-ds-base';
import { cn, useAnalytics } from '@restworld/utils-common';

import {
  ShareCopyLinkIcon,
  ShareWhatsappIcon,
  ShareTelegramIcon,
  ShareLinkedInIcon,
  ShareInstagramIcon,
  ShareFacebookIcon,
} from './share-icons';

/** Where the share modal was opened from — sent as `source` on referral share events. */
export type ReferralShareSource = 'sticky_cta' | 'suggested_venues' | 'offline_jp_modal';

interface ShareLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  referralLink: string;
  source: ReferralShareSource;
}

type CopySource = 'copy' | 'instagram';

/**
 * Share referral link modal -- 2-column grid with copy-to-clipboard
 * and deep-links for WhatsApp, Telegram, LinkedIn, Instagram, Facebook.
 * Uses OpenGraph for social previews and open-in-app links where possible.
 */
export function ShareLinkModal({ isOpen, onClose, referralLink, source }: ShareLinkModalProps) {
  const { t } = useTranslation('referral-page');
  const { trackEvent } = useAnalytics();
  /** Which row triggered copy -- so "link copied" shows on that row only (copy vs Instagram). */
  const [copiedSource, setCopiedSource] = useState<CopySource | null>(null);

  useEffect(() => {
    if (!isOpen) setCopiedSource(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    trackEvent('referral_share_modal_opened', { source });
  }, [isOpen, source, trackEvent]);

  const trackChannelClick = useCallback(
    (channel: string) => {
      trackEvent('referral_share_channel_clicked', { channel, source });
    },
    [trackEvent, source]
  );

  const handleCopyLink = useCallback(
    async (copySource: CopySource) => {
      try {
        await navigator.clipboard.writeText(referralLink);
      } catch {
        const textArea = document.createElement('textarea');
        textArea.value = referralLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedSource(copySource);
      setTimeout(() => setCopiedSource(null), 2000);
    },
    [referralLink]
  );

  const encodedLink = encodeURIComponent(referralLink);

  const shareOptions = [
    {
      key: 'copy' as const,
      channel: 'copy_link',
      label: copiedSource === 'copy' ? t('share_modal_link_copied') : t('share_modal_copy_link'),
      description: t('share_modal_copy_link_description'),
      Icon: copiedSource === 'copy' ? CheckCircleIcon : ShareCopyLinkIcon,
      onClick: () => handleCopyLink('copy'),
    },
    {
      key: 'whatsapp',
      channel: 'whatsapp',
      label: t('share_modal_whatsapp'),
      description: t('share_modal_whatsapp_description'),
      Icon: ShareWhatsappIcon,
      onClick: () => window.open(`https://api.whatsapp.com/send?text=${encodedLink}`, '_blank'),
    },
    {
      key: 'telegram',
      channel: 'telegram',
      label: t('share_modal_telegram'),
      description: t('share_modal_telegram_description'),
      Icon: ShareTelegramIcon,
      onClick: () => window.open(`https://t.me/share/url?url=${encodedLink}`, '_blank'),
    },
    {
      key: 'linkedin',
      channel: 'linkedin',
      label: t('share_modal_linkedin'),
      description: t('share_modal_linkedin_description'),
      Icon: ShareLinkedInIcon,
      onClick: () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodedLink}`, '_blank'),
    },
    {
      key: 'instagram' as const,
      channel: 'instagram',
      label: copiedSource === 'instagram' ? t('share_modal_link_copied') : t('share_modal_instagram'),
      description: t('share_modal_instagram_description'),
      Icon: copiedSource === 'instagram' ? CheckCircleIcon : ShareInstagramIcon,
      onClick: () => handleCopyLink('instagram'),
    },
    {
      key: 'facebook',
      channel: 'facebook',
      label: t('share_modal_facebook'),
      description: t('share_modal_facebook_description'),
      Icon: ShareFacebookIcon,
      onClick: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedLink}`, '_blank'),
    },
  ];

  return (
    <Modal open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <ModalContent className="w-full max-w-[640px]" noPadding>
        <OptionsGrid>
          {shareOptions.map(({ key, channel, label, description, Icon, onClick }) => (
            <ShareCard key={key}>
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => {
                  trackChannelClick(channel);
                  onClick();
                }}
                className="!shadow-none !bg-transparent !h-10 !px-2 !py-1 !font-medium !text-base !text-[#1C1C33] !justify-start"
              >
                <ActionContent>
                  <span>{label}</span>
                  <Icon
                    className={
                      (key === 'copy' || key === 'instagram') && copiedSource === key
                        ? 'h-4 w-4 shrink-0 text-green-500'
                        : 'h-4 w-4 shrink-0'
                    }
                  />
                </ActionContent>
              </Button>
              <OptionDescription>{description}</OptionDescription>
            </ShareCard>
          ))}
        </OptionsGrid>
      </ModalContent>
    </Modal>
  );
}

export default ShareLinkModal;

// Layout Components

/** 2-column, 3-row grid matching Figma layout */
const OptionsGrid = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('grid grid-cols-1 sm:grid-cols-2 gap-2.5 w-full pt-5 pb-8 sm:pb-8 px-6', className)} {...props} />
);

/** Non-interactive card container */
const ShareCard = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('flex flex-col gap-1 items-start p-4 border border-[#DEDEE1] rounded-2xl', className)}
    {...props}
  />
);

/** Inline-flex wrapper to keep label + icon in a row at 16px */
const ActionContent = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('inline-flex items-center gap-2', className)} {...props} />
);

const OptionDescription = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-sm font-medium text-[#666676] pl-2', className)} {...props} />
);
